import { isFileDownloadCommand } from 'core/api/api.typesGuards';
import { openAPIWrapper } from 'core/api/openApi';
import type {   AlignmentOptions,   EntitySequence as Sequence, SaveSequenceFeatureColorRequest,
  SelectedColumns as SelectionPosition,
  SequenceAlign, SequenceType,   TextFile as FileContent} from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import {
AlignApi,
ColorsApi, DNAApi,
ExportApi, SessionApi,
  WSAApi,
} from 'sequenceAnalyser/api/sequenceAnalyserApiClient';

export async function getSession(id: string) {
  return openAPIWrapper(SessionApi)(async (api) => {
    const response = await api.sequenceAnalyzerSession({
      seqAnalSessionId: id,
    });

    if (response.payload && response.payload.id) {
      return response;
    }

    return Promise.reject();
  });
}

export async function alignSequences(sequences: SequenceAlign[], options: AlignmentOptions) {
  return openAPIWrapper(AlignApi)(async (api) => {
    if (sequences.length < 2) {
      return sequences;
    }

    const size = new TextEncoder().encode(JSON.stringify(sequences)).length;
    if (size > 1024 * 1024 * 20) {
      return Promise.reject(new Error('The size of the sequences is too large'));
    }

    const response = await api.align({
      sequencesAlignRequest: {
        sequences,
        alignmentOptions: options,
      },
    });

    if (response.payload?.sequences) {
      const newAlignedSequences: SequenceAlign[] = response.payload.sequences.map(({ id, residues }) => ({
        id,
        residues: residues.replace(/[-]/g, '.'),
      }));

      // Ensure sequences are sorted in the same order as they were sent
      return sequences.map(({ id }) => newAlignedSequences.find((as) => as.id === id) as SequenceAlign);
    }

    return Promise.reject();
  });
}

export async function uploadFile(sessionId: string, fileContent: FileContent) {
  return openAPIWrapper(SessionApi)(async (api) => {
    const response = await api.uploadFile({
      seqAnalSessionId: sessionId,
      openFileRequest: {
        fileContent,
      },
    });

    if (response.payload && response.payload.entitySequences) {
      const newAlignedSequences: SequenceAlign[] = response.payload.entitySequences.map(({ id, residues }) => ({
        id,
        residues: residues || '',
      }));

      return {
        sequences: response.payload.entitySequences,
        alignedSequences: response.payload.alignedSequences || newAlignedSequences,
      };
    }

    return Promise.reject();
  });
}

export async function arrangeBySimilarity(sequences: SequenceAlign[], options: AlignmentOptions) {
  return openAPIWrapper(AlignApi)(async (api) => {
    const response = await api.order({
      sequencesAlignRequest: {
        sequences,
        alignmentOptions: options,
      },
    });

    if (response.payload && response.payload.sequences) {
      return response.payload.sequences;
    }

    return Promise.reject();
  });
}

export async function exportFile(
  sessionId: string,
  fileType: string,
  sequences: Sequence[],
  alignedSequences: SequenceAlign[],
  selection?: SelectionPosition,
) {
  return openAPIWrapper(ExportApi)(async (api) => {
    const response = await api.save({
      seqAnalSessionId: sessionId,
      saveRequest: {
        format: fileType,
        entitySequences: sequences,
        alignedSequences,
        selection,
      },
    });

    const { payload } = response;

    if (isFileDownloadCommand(payload)) {
      return payload;
    }

    return Promise.reject();
  });
}

export async function oppositeStrands(sessionId: string, sequences: Sequence[]) {
  return openAPIWrapper(DNAApi)(async (api) => {
    const response = await api.toggleStrands({
      seqAnalSessionId: sessionId,
      strandsRequest: {
        entitySequences: sequences,
      },
    });

    if (response.payload && response.payload.entitySequences) {
      return response.payload.entitySequences;
    }

    return Promise.reject();
  });
}

export async function similarityTree(sequences: SequenceAlign[]) {
  return openAPIWrapper(AlignApi)(async (api) => {
    const response = await api.tree({
      similarityTreeRequest: {
        sequences,
      },
    });

    if (response.payload) {
      return response.payload;
    }

    return Promise.reject();
  });
}

export async function sequenceFeatureColors(sequenceType: SequenceType) {
  return openAPIWrapper(ColorsApi)(async (api) => {
    const response = await api.colors({ sequenceType });

    if (response.payload) {
      return response.payload;
    }

    return Promise.reject();
  });
}

export async function saveSequenceFeatureColor(color: SaveSequenceFeatureColorRequest) {
  return openAPIWrapper(ColorsApi)(async (api) => {
    const response = await api.saveColor({
      saveSequenceFeatureColorRequest: color,
    });

    if (response.payload) {
      return response.payload;
    }

    return Promise.reject();
  });
}

export async function createHitList(sessionId: string, selectedIds: string[]) {
  return openAPIWrapper(WSAApi)(async (api) => {
    await api.hitlist({
      sessionId,
      hitListRequest: {
        ids: selectedIds,
      },
    });

    return Promise.reject();
  });
}
